import { useState } from "react";
import { Link } from "react-router";
import { ArrowLeft, Pencil, Plus, RotateCcw, Save, Trash2, X } from "lucide-react";
import {
  Body,
  Card,
  CardTitle,
  PageHeader,
  Subtitle,
  VButton,
  VDialog,
  VDialogContent,
  VField,
  VInput,
  cn,
} from "@/components/ui";
import type { AssetType, CustomAssetType } from "@/domains/assets";
import {
  ASSET_ICON_OPTIONS,
  AssetTypeIcon,
  DEFAULT_ASSET_TYPE_LABELS,
  getIconByKey,
  useAssetTypeLabels,
  useCustomAssetTypes,
} from "@/domains/assets";

const TYPE_ORDER: ReadonlyArray<AssetType> = [
  "camera",
  "mobileTree",
  "camcar",
  "batteryPack",
  "trailer",
];

type DialogState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; original: CustomAssetType };

export function AssetTypesPage(): React.JSX.Element {
  const [labels, setLabels] = useAssetTypeLabels();
  const [customs, setCustoms] = useCustomAssetTypes();
  const [editingBuiltIn, setEditingBuiltIn] = useState<AssetType | null>(null);
  const [draft, setDraft] = useState("");
  const [dialog, setDialog] = useState<DialogState>({ kind: "closed" });

  const handleStartEdit = (type: AssetType): void => {
    setEditingBuiltIn(type);
    setDraft(labels[type]);
  };

  const handleSaveBuiltIn = (): void => {
    if (!editingBuiltIn) return;
    const trimmed = draft.trim();
    if (trimmed.length === 0) return;
    setLabels({ ...labels, [editingBuiltIn]: trimmed });
    setEditingBuiltIn(null);
    setDraft("");
  };

  const handleCancelBuiltIn = (): void => {
    setEditingBuiltIn(null);
    setDraft("");
  };

  const handleResetAll = (): void => {
    setLabels({ ...DEFAULT_ASSET_TYPE_LABELS });
    setEditingBuiltIn(null);
  };

  const handleCreateCustom = (input: CustomAssetType): void => {
    setCustoms([...customs, input]);
    setDialog({ kind: "closed" });
  };

  const handleUpdateCustom = (original: CustomAssetType, next: CustomAssetType): void => {
    setCustoms(customs.map((c) => (c.code === original.code ? next : c)));
    setDialog({ kind: "closed" });
  };

  const handleDeleteCustom = (custom: CustomAssetType): void => {
    const ok = window.confirm(`Type "${custom.label}" verwijderen?`);
    if (!ok) return;
    setCustoms(customs.filter((c) => c.code !== custom.code));
  };

  const hasOverrides = TYPE_ORDER.some(
    (t) => labels[t] !== DEFAULT_ASSET_TYPE_LABELS[t],
  );

  const reservedCodes = new Set<string>([
    ...TYPE_ORDER,
    ...customs.map((c) => c.code),
  ]);

  return (
    <div className="space-y-6">
      <Link
        to="/configuration"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Terug naar configuratie
      </Link>

      <PageHeader
        title="Asset-types"
        subtitle="Beheer de types van assets die kunnen worden ingezet en hun labels in het platform."
        actions={
          hasOverrides ? (
            <VButton variant="outline" onClick={handleResetAll}>
              <RotateCcw className="size-4" aria-hidden />
              Standaardlabels herstellen
            </VButton>
          ) : null
        }
      />

      <Card padding="lg" className="space-y-3">
        <CardTitle>Standaardtypes</CardTitle>
        <Subtitle>
          De vijf voorgedefinieerde types. Labels zijn aanpasbaar; codes en iconen zijn
          vast.
        </Subtitle>
        <ul className="divide-y divide-border">
          {TYPE_ORDER.map((type) => {
            const isEditing = editingBuiltIn === type;
            const label = labels[type];
            const defaultLabel = DEFAULT_ASSET_TYPE_LABELS[type];
            const isOverridden = label !== defaultLabel;
            return (
              <li key={type} className="flex items-center gap-3 py-3">
                <span className="inline-flex size-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <AssetTypeIcon type={type} className="size-5" />
                </span>
                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <VInput
                      id={`label-${type}`}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      autoFocus
                      aria-label={`Label voor type ${defaultLabel}`}
                    />
                  ) : (
                    <>
                      <Body weight="semibold">{label}</Body>
                      <Subtitle>
                        Type-code: <code className="font-mono text-xs">{type}</code>
                        {isOverridden ? (
                          <span className="ml-2 text-warning-700">
                            (aangepast van &ldquo;{defaultLabel}&rdquo;)
                          </span>
                        ) : null}
                      </Subtitle>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {isEditing ? (
                    <>
                      <VButton
                        size="sm"
                        onClick={handleSaveBuiltIn}
                        disabled={draft.trim().length === 0}
                      >
                        <Save className="size-4" aria-hidden />
                        Opslaan
                      </VButton>
                      <VButton variant="ghost" size="sm" onClick={handleCancelBuiltIn}>
                        <X className="size-4" aria-hidden />
                        Annuleren
                      </VButton>
                    </>
                  ) : (
                    <VButton
                      variant="ghost"
                      size="sm"
                      onClick={() => handleStartEdit(type)}
                    >
                      <Pencil className="size-4" aria-hidden />
                      Bewerken
                    </VButton>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      <Card padding="lg" className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Eigen types</CardTitle>
            <Subtitle>
              Voeg extra types toe voor uitbreidingen zoals drones, bodycams of sensoren.
            </Subtitle>
          </div>
          <VButton onClick={() => setDialog({ kind: "create" })}>
            <Plus className="size-4" aria-hidden />
            Nieuw type toevoegen
          </VButton>
        </div>

        {customs.length === 0 ? (
          <Body tone="muted" className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-6 text-center">
            Nog geen eigen types toegevoegd.
          </Body>
        ) : (
          <ul className="divide-y divide-border">
            {customs.map((ct) => {
              const Icon = getIconByKey(ct.iconKey);
              return (
                <li key={ct.code} className="flex items-center gap-3 py-3">
                  <span className="inline-flex size-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <Icon className="size-5" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <Body weight="semibold">{ct.label}</Body>
                    <Subtitle>
                      Type-code: <code className="font-mono text-xs">{ct.code}</code>
                    </Subtitle>
                  </div>
                  <div className="flex items-center gap-1">
                    <VButton
                      variant="ghost"
                      size="sm"
                      onClick={() => setDialog({ kind: "edit", original: ct })}
                    >
                      <Pencil className="size-4" aria-hidden />
                      Bewerken
                    </VButton>
                    <VButton
                      variant="ghost"
                      size="icon"
                      aria-label="Verwijderen"
                      onClick={() => handleDeleteCustom(ct)}
                      icon={<Trash2 className="size-4 text-error-700" aria-hidden />}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {dialog.kind !== "closed" ? (
        <CustomAssetTypeDialog
          mode={dialog}
          reservedCodes={reservedCodes}
          onClose={() => setDialog({ kind: "closed" })}
          onCreate={handleCreateCustom}
          onUpdate={handleUpdateCustom}
        />
      ) : null}
    </div>
  );
}

type CustomAssetTypeDialogProps = {
  mode: { kind: "create" } | { kind: "edit"; original: CustomAssetType };
  reservedCodes: ReadonlySet<string>;
  onClose: () => void;
  onCreate: (input: CustomAssetType) => void;
  onUpdate: (original: CustomAssetType, next: CustomAssetType) => void;
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 24);
}

function CustomAssetTypeDialog({
  mode,
  reservedCodes,
  onClose,
  onCreate,
  onUpdate,
}: CustomAssetTypeDialogProps): React.JSX.Element {
  const isEdit = mode.kind === "edit";
  const initial =
    mode.kind === "edit"
      ? { label: mode.original.label, code: mode.original.code, iconKey: mode.original.iconKey }
      : { label: "", code: "", iconKey: ASSET_ICON_OPTIONS[0]?.key ?? "box" };

  const [label, setLabel] = useState(initial.label);
  const [code, setCode] = useState(initial.code);
  const [codeTouched, setCodeTouched] = useState(isEdit);
  const [iconKey, setIconKey] = useState(initial.iconKey);

  const effectiveCode = codeTouched ? code.trim() : slugify(label);
  const trimmedLabel = label.trim();
  const codeIsValid =
    effectiveCode.length > 0 && /^[a-z][a-z0-9]*$/.test(effectiveCode);
  const codeIsConflict =
    codeIsValid &&
    effectiveCode !== (mode.kind === "edit" ? mode.original.code : null) &&
    reservedCodes.has(effectiveCode);
  const isValid = trimmedLabel.length > 0 && codeIsValid && !codeIsConflict;

  const handleSubmit = (): void => {
    if (!isValid) return;
    const next: CustomAssetType = {
      code: effectiveCode,
      label: trimmedLabel,
      iconKey,
    };
    if (mode.kind === "edit") onUpdate(mode.original, next);
    else onCreate(next);
  };

  return (
    <VDialog open onOpenChange={(open) => !open && onClose()}>
      <VDialogContent
        title={isEdit ? "Type bewerken" : "Nieuw asset-type"}
        description="Geef een label, een unieke code en kies een passend icoon."
      >
        <div className="space-y-4">
          <VField label="Label" htmlFor="ct-label" required>
            <VInput
              id="ct-label"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                if (!codeTouched && !isEdit) setCode(slugify(e.target.value));
              }}
              autoFocus
              placeholder="Bv. Drone"
            />
          </VField>

          <VField
            label="Type-code"
            htmlFor="ct-code"
            required
            hint={
              isEdit
                ? "Codes van bestaande types kunnen niet meer gewijzigd worden in dit prototype."
                : "Lowercase, geen spaties — wordt automatisch afgeleid van het label, kan aangepast worden."
            }
            error={
              codeIsConflict
                ? "Deze code is al in gebruik."
                : !codeIsValid && effectiveCode.length > 0
                  ? "Gebruik enkel letters en cijfers; start met een letter."
                  : undefined
            }
          >
            <VInput
              id="ct-code"
              value={effectiveCode}
              onChange={(e) => {
                setCodeTouched(true);
                setCode(e.target.value);
              }}
              disabled={isEdit}
              placeholder="bv. drone"
            />
          </VField>

          <VField label="Icoon" htmlFor="ct-icon">
            <div className="grid grid-cols-5 gap-2" id="ct-icon" role="radiogroup">
              {ASSET_ICON_OPTIONS.map((opt) => {
                const Icon = getIconByKey(opt.key);
                const isSelected = iconKey === opt.key;
                return (
                  <button
                    type="button"
                    key={opt.key}
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={opt.label}
                    title={opt.label}
                    onClick={() => setIconKey(opt.key)}
                    className={cn(
                      "flex aspect-square cursor-pointer items-center justify-center rounded-md border transition-colors",
                      isSelected
                        ? "border-primary-600 bg-primary-50 ring-2 ring-primary-200"
                        : "border-border bg-card hover:border-primary-300 hover:bg-muted/40",
                    )}
                  >
                    <Icon
                      className={cn(
                        "size-5",
                        isSelected ? "text-primary-700" : "text-muted-foreground",
                      )}
                      aria-hidden
                    />
                  </button>
                );
              })}
            </div>
          </VField>

          <div className="flex justify-end gap-2 pt-2">
            <VButton variant="ghost" onClick={onClose}>
              Annuleren
            </VButton>
            <VButton onClick={handleSubmit} disabled={!isValid}>
              <Save className="size-4" aria-hidden />
              {isEdit ? "Opslaan" : "Toevoegen"}
            </VButton>
          </div>
        </div>
      </VDialogContent>
    </VDialog>
  );
}
